import React, { useState, useEffect } from 'react';
import { Calendar, ArrowRight, X, Truck, TrendingUp, AlertTriangle, Package, Bell } from 'lucide-react';
import { getNewsItems, formatNewsDate, NewsItem } from '../services/newsService';
import { Timestamp } from 'firebase/firestore';

const CATEGORY_ICONS: Record<string, React.ElementType> = {
  Logistics: Truck,
  'Market Opportunity': AlertTriangle,
  'Industry Trends': TrendingUp,
  'Product Launch': Package,
  Alert: Bell,
};

const CATEGORY_COLOR: Record<string, string> = {
  Logistics: 'text-steel border-steel/30',
  'Market Opportunity': 'text-ember border-ember/30',
  'Industry Trends': 'text-olive border-olive/30',
  'Product Launch': 'text-ember-deep border-ember-deep/30',
  Alert: 'text-ember border-ember/40',
};

const STATIC_NEWS: NewsItem[] = [
  {
    id: 'n00',
    category: 'Logistics',
    date: 'October 8, 2026',
    title: "We're in — 345 Nugget Avenue is live",
    summary:
      'The move is done. Every Incolma blade we distribute now ships from our own warehouse at 345 Nugget Avenue, Unit 17, Scarborough — and free order pickup is open by appointment for retail and wholesale customers. Grand opening date for the retail counter coming soon.',
    fullContent: `The racks are up, the containers are unloaded, and The Machete Man officially operates from 345 Nugget Avenue, Unit 17, Scarborough.

What's live as of today:

— **Free order pickup.** Choose "Warehouse pickup" at checkout and we'll confirm your appointment time on WhatsApp once payment clears. Government photo ID at handoff, as always.

— **Wholesale will-call.** GTA wholesale accounts can collect orders directly from the warehouse — message us on WhatsApp to book a slot, or add it to your next order.

— **Deeper stock.** With our own receiving and racking, standing inventory across the catalog is the strongest it has ever been heading into fall clearing season.

Still to come: the public retail counter's grand opening. We're setting the date now — wholesale accounts and newsletter subscribers hear first, and the opening-day offer will be worth the trip.

Questions or pickup bookings: WhatsApp +1 (416) 684-0010.`,
    createdAt: Timestamp.fromDate(new Date('2026-10-08T12:00:00')),
    published: true,
  },
  {
    id: 'n0',
    category: 'Logistics',
    date: 'September 1, 2026',
    title: 'The Machete Man secures a Scarborough warehouse & retail home',
    summary:
      'We\'ve acquired a combined warehouse and retail space in Scarborough, Ontario — move-in is October 1. It becomes the fulfillment hub for every Incolma blade we distribute across Canada, with a retail counter and wholesale will-call pickup to follow. Grand-opening date announced after we\'re in.',
    fullContent: `The Machete Man has acquired a combined warehouse and retail location in Scarborough, Ontario. We take possession and begin the move on October 1, 2026.

What this means for our partners and customers:

— **One roof for the whole operation.** As Incolma's exclusive Canadian distributor and wholesaler, every blade that enters the country will now be received, inspected, and dispatched from our own Scarborough facility.

— **Wholesale will-call pickup.** GTA-area wholesale accounts will be able to collect orders directly from the warehouse — skip the courier, grab your stock the same day it's ready.

— **A retail counter.** For the first time, walk-in customers will be able to handle the blades before buying. Retail hours will be published closer to opening.

— **Deeper stock, faster turnaround.** Container receiving into our own warehouse means larger standing inventory across the full catalog and quicker fulfillment on both retail and wholesale orders.

We are also preparing a visit to Incolma's operations in Colombia in the coming weeks — walking the forge floor in Manizales, reviewing the lines we carry, and planning the next additions to the catalog.

A grand-opening launch date will be announced once we've moved in and the racks are stocked. Wholesale accounts will hear first — watch this page and your inbox.

Questions in the meantime? WhatsApp us at +1 (416) 684-0010.`,
    createdAt: Timestamp.fromDate(new Date('2026-09-01T12:00:00')),
    published: true,
  },
  {
    id: 'n1',
    category: 'Alert',
    date: 'May 28, 2026',
    title: 'Manitoba Bill 39 — what machete retailers need to know',
    summary:
      'Manitoba has tabled the Long-Bladed Weapons Control Act, regulating retail sale of machetes and knives with blades over 30 cm. Here\'s what changes, when, and how to stay compliant if you ship to or operate in Manitoba.',
    fullContent: `The Manitoba government has tabled Bill 39 — the Long-Bladed Weapons Control Act — regulating the retail sale of machetes, swords, and knives with blades exceeding 30 cm.

Retailers selling these tools in Manitoba, or shipping to Manitoba customers, will be required to comply with new identity-verification and sale-record requirements once the regulations are gazetted. The Retail Council of Canada is monitoring rollout closely on behalf of member retailers nationwide.

For partners outside Manitoba: this is currently a single-province regulation, but federal attention is rising. Several MPs have publicly referenced Bill 39 as a model. We are tracking developments in every province and will notify retailers directly if similar legislation is introduced where you operate.

What you can do today:
— If you sell to Manitoba customers via mail-order, prepare a customer-verification workflow ahead of fall enforcement.
— If you operate a Manitoba retail location, review the Retail Council guidance once final regulations release.
— For all retailers nationwide: most jurisdictions still treat working agricultural tools as exempt — but local enforcement practice varies. When in doubt, display the Incolma certificate of origin and your wholesale account verification.

We will share the final Manitoba regulation language and a one-page retailer compliance brief with all active wholesale accounts once regulations are gazetted.`,
    createdAt: Timestamp.fromDate(new Date('2026-05-28T12:00:00')),
    published: true,
  },
  {
    id: 'n2',
    category: 'Market Opportunity',
    date: 'May 20, 2026',
    title: 'Caribana 2026 — your pre-July stocking window',
    summary:
      'North America\'s largest Caribbean carnival returns to Toronto July 30 – August 3. For Caribbean and West Indian grocers in the GTA, the four weeks before Caribana are the strongest stocking window of the year for utility blades.',
    fullContent: `Toronto Caribbean Carnival celebrates its 59th year this summer, with the Grand Parade running Saturday August 1 along Exhibition Place and Lakeshore Boulevard.

For our retail partners serving the Caribbean and West Indian diaspora, the four weeks leading into Caribana are consistently the strongest stocking window on the calendar. Extended family arrives from the islands, household kitchens stock up for Curry Goat and Rice & Peas weekends, and machetes move alongside green coconuts, ackee, salt fish, and yam.

Recommended pre-Caribana display strategy:
— Stock the 18" and 20" Latin profiles (Incolma 706, Incolma 127) near produce — these are the everyday cutlasses your customers know on sight.
— Place the smaller 14" agricultural blades near coconut and kitchen tools — these move on impulse during carnival week.
— A small printed card noting "drop-forged Colombian carbon steel · Incolma since 1960" sits well next to the blades. Your customer base already recognizes the brand language.

Order by July 5 to guarantee delivery before the first weekend of the festival. After that, courier networks across the GTA slow noticeably with parade-week traffic and road closures.`,
    createdAt: Timestamp.fromDate(new Date('2026-05-20T12:00:00')),
    published: true,
  },
  {
    id: 'n3',
    category: 'Market Opportunity',
    date: 'May 15, 2026',
    title: 'Phragmites & Buckthorn — Ontario\'s summer control season is open',
    summary:
      'With invasive Phragmites and Common Buckthorn in active leaf across Ontario, the Ontario Invasive Plant Council is again recommending manual cutting as the preferred control method. Garden, outdoor, and conservation-adjacent retailers can pair eco-minded customers with the right blade.',
    fullContent: `Manual control of invasive plants is the recommended approach across Ontario's conservation authorities this season. The Ontario Invasive Plant Council's technical bulletin notes that cutting works best "after rain when the soil is soft and pliable" — and that branches bearing berries should be removed before late summer to prevent seed spread.

For retailers in garden, outdoor, and conservation-adjacent segments, this is a multi-month sales window — running from now through October.

Recommended blade by use case:
— **Light brush and seedlings:** Incolma 26620 (12–14" agricultural) handles young Buckthorn cleanly.
— **Mature Buckthorn and dense Phragmites stands:** Incolma 706 or 127 (18–20" Latin profiles) deliver the leverage and tip mass for thicker stems — without forcing customers into a chainsaw purchase.
— **Riparian and trail clearing:** Incolma 152 (22" Peinilla) is the right choice where reach matters and the blade needs to slip through cattail and reed beds.

Pair any of these with our Mill File Box (12-pack) and position the bundle as a "Caretaker Kit" near checkout. The Nature Conservancy of Canada and Western Weed Control have both been actively promoting manual cutting on social channels this season — your conservation-minded customers are already primed.`,
    createdAt: Timestamp.fromDate(new Date('2026-05-15T12:00:00')),
    published: true,
  },
  {
    id: 'n4',
    category: 'Product Launch',
    date: 'May 10, 2026',
    title: 'Incolma 202T joins the wholesale catalog',
    summary:
      'The Incolma 202T (18-inch) is now available to our wholesale partners — a clean working blade that complements the 152 Natural and 127 Polished profiles. Initial allocation is limited; priority handled in retailer application order.',
    fullContent: `The Incolma 202T is now in the catalog. The 18-inch profile sits between the 152 Natural (raw-forged everyday workhorse) and the 127 Polished (classic single-edge Latin) — same SAE 1075 high-carbon steel, same Colombian forging tradition, with a balance customers favor when stepping up from a shorter everyday blade to a longer agricultural one.

What's distinctive about the 202T:
— **18" blade length** — long enough for serious clearing work, short enough for trail-pack carry.
— **Drop-forged in Manizales** — same Incolma line that's been running since 1960.
— **Full tang, polymer handle** — all-weather grip, holds up to wet and humid conditions retailers in coastal and tropical-diaspora markets see.
— **Catalog reference:** Incolma 202T. Wholesale pricing on request via verified retailer application.

Initial allocation is limited. Existing wholesale accounts may add the 202T to their next standard order at no priority cost. New retailers applying through themacheteman.com/apply can request the 202T as part of their initial mix-and-match selection.

Full specifications and product photography are on the Catalog page. WhatsApp +1 (416) 684-0010 if you would like a sample handled before placing a full order.`,
    createdAt: Timestamp.fromDate(new Date('2026-05-10T12:00:00')),
    published: true,
  },
  {
    id: 'n5',
    category: 'Industry Trends',
    date: 'Apr 30, 2026',
    title: 'Why Colombia still makes the world\'s working machete',
    summary:
      'Colombia exports more agricultural machetes than any other country on earth. Here\'s why the Colombian forging tradition — drop-forged, hardened, tempered — sits at the center of the "buy for life" tool resurgence your customers are responding to.',
    fullContent: `ProColombia's B2B trade marketplace lists Colombian-forged machetes under HS code 8201401000 — and the export data tells a clear story. Colombia is, by volume, the world's largest exporter of agricultural cutlasses. Our partners at Incolma have been forging at that scale since 1960, supplying every major coffee, sugar-cane, and banana-producing region across the Americas.

What separates Colombian forging from imported tactical brands:
— **Drop-forged from a single billet** — not stamped from cold-rolled stock. The blade is grain-aligned for impact strength.
— **High-carbon SAE 1075 steel** — hardenable to 52–58 HRC depending on profile. Sharpens with a file, holds an edge across a full working day.
— **Hardened AND tempered** — not just hardened. Tempering pulls back brittleness while preserving edge retention. This is the reason a Colombian working blade outlasts three or four imported "tactical" machetes in actual field use.

The "buy for life" consumer movement has accelerated noticeably across 2026 — your customers are increasingly skeptical of disposable stamped-steel tools and reaching for serviceable carbon steel they can maintain. Position drop-forged Colombian machetes as the heritage answer to this shift. Display a Mill File alongside the blades — sharpenable tools convert better when the maintenance kit is visible at point of purchase.`,
    createdAt: Timestamp.fromDate(new Date('2026-04-30T12:00:00')),
    published: true,
  },
];

const NewsPage: React.FC = () => {
  const [newsItems, setNewsItems] = useState<NewsItem[]>([]);
  const [selectedNews, setSelectedNews] = useState<NewsItem | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchNews() {
      try {
        const items = await getNewsItems();
        setNewsItems(items.length > 0 ? items : STATIC_NEWS);
      } catch (error) {
        console.error('Error fetching news:', error);
        setNewsItems(STATIC_NEWS);
      } finally {
        setLoading(false);
      }
    }
    fetchNews();
  }, []);

  useEffect(() => {
    if (!selectedNews) return;
    const handler = (e: KeyboardEvent) => e.key === 'Escape' && setSelectedNews(null);
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [selectedNews]);

  const getDisplayDate = (item: NewsItem): string => {
    if (item.createdAt && typeof item.createdAt.toDate === 'function') {
      return formatNewsDate(item.createdAt);
    }
    return item.date || '';
  };

  return (
    <div className="bg-paper min-h-screen pb-24">

      {/* Header */}
      <header className="bg-forge text-paper py-24 lg:py-28 px-6 lg:px-10 relative">
        <div className="max-w-editorial mx-auto">
          <p className="font-mono text-[11px] tracking-micro uppercase text-ember-soft mb-6">
            — B2B intelligence
          </p>
          <h1 className="font-editorial italic font-light text-5xl lg:text-6xl text-paper-warm leading-[1.05] tracking-tight mb-5 max-w-prose">
            News & announcements.
          </h1>
          <p className="text-paper/70 text-lg max-w-prose">
            Supply chain updates, regulatory alerts, and product launches directly from the source.
          </p>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-brass/40 to-transparent" />
      </header>

      {/* Grid */}
      <div className="max-w-editorial mx-auto px-6 lg:px-10 py-16 lg:py-20">
        {loading ? (
          <div className="flex justify-center py-24">
            <div className="w-8 h-8 border-2 border-ink-200 border-t-ember rounded-full animate-spin" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-10">
            {newsItems.map((item) => {
              const IconComponent = CATEGORY_ICONS[item.category] || Bell;
              const categoryColor = CATEGORY_COLOR[item.category] || 'text-ink-700 border-ink-300';

              return (
                <article
                  key={item.id}
                  className="bg-paper-bright border border-ink-200 hover:border-ink-300 transition-colors duration-300 ease-editorial group flex flex-col"
                >
                  <div className="p-7 lg:p-8 flex-1">
                    <div className="flex justify-between items-start mb-6 gap-3">
                      <span
                        className={`inline-flex items-center gap-1.5 font-mono text-[10px] tracking-micro uppercase border px-2 py-0.5 ${categoryColor}`}
                      >
                        <IconComponent className="w-3 h-3" strokeWidth={1.5} />
                        {item.category}
                      </span>
                      <span className="inline-flex items-center gap-1 font-mono text-[10px] tracking-wider text-ink-500 flex-shrink-0">
                        <Calendar className="w-3 h-3" strokeWidth={1.5} />
                        {getDisplayDate(item)}
                      </span>
                    </div>

                    <h3 className="font-editorial italic text-2xl text-ink-950 mb-4 tracking-tight leading-tight group-hover:text-ember transition-colors duration-300 ease-editorial">
                      {item.title}.
                    </h3>

                    <p className="text-ink-700 text-sm leading-[1.65]">{item.summary}</p>
                  </div>

                  <div className="px-7 lg:px-8 pb-7 lg:pb-8 mt-auto">
                    <button
                      onClick={() => setSelectedNews(item)}
                      className="inline-flex items-center gap-2 text-ember hover:text-ember-deep font-medium text-sm transition-colors duration-150 ease-editorial group/btn"
                    >
                      Read full bulletin
                      <ArrowRight
                        className="w-3.5 h-3.5 transition-transform duration-200 ease-editorial group-hover/btn:translate-x-1"
                        strokeWidth={1.5}
                      />
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>

      {/* Modal */}
      {selectedNews && (
        <div className="fixed inset-0 bg-forge-deep/70 z-50 flex items-center justify-center p-4 lg:p-8 backdrop-blur-sm animate-in fade-in duration-300 ease-editorial">
          <div className="relative bg-paper-bright w-full max-w-3xl max-h-[88vh] overflow-y-auto shadow-float animate-in zoom-in-95 duration-300 ease-editorial">
            <button
              onClick={() => setSelectedNews(null)}
              className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center bg-paper-bright/90 hover:bg-paper text-ink-700 hover:text-ink-950 border border-ink-200 transition-colors duration-150 z-10"
              aria-label="Close"
            >
              <X className="w-4 h-4" strokeWidth={1.5} />
            </button>

            <div className="p-8 md:p-12 lg:p-14">
              <div className="flex items-center gap-3 mb-8">
                <span
                  className={`inline-flex items-center gap-1.5 font-mono text-[10px] tracking-micro uppercase border px-2 py-0.5 ${CATEGORY_COLOR[selectedNews.category] || 'text-ink-700 border-ink-300'}`}
                >
                  {selectedNews.category}
                </span>
                <span className="inline-flex items-center gap-1 font-mono text-[10px] tracking-wider text-ink-500">
                  <Calendar className="w-3 h-3" strokeWidth={1.5} />
                  {getDisplayDate(selectedNews)}
                </span>
              </div>

              <h2 className="font-editorial italic font-light text-4xl lg:text-5xl text-ink-950 leading-[1.1] tracking-tight mb-10">
                {selectedNews.title}.
              </h2>

              <div className="max-w-prose">
                {selectedNews.fullContent.split('\n').map(
                  (paragraph, idx) =>
                    paragraph.trim() && (
                      <p key={idx} className="mb-5 text-ink-700 leading-[1.7] text-base">
                        {paragraph.trim()}
                      </p>
                    )
                )}
              </div>

              <div className="mt-12 pt-8 border-t border-ink-200 flex justify-end">
                <button
                  onClick={() => setSelectedNews(null)}
                  className="bg-forge hover:bg-forge-deep text-paper-warm px-7 py-3 text-sm font-medium transition-colors duration-150 ease-editorial"
                >
                  Close bulletin
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewsPage;
